import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    type ApplicationRegisterFilters,
    createApplicationRegister,
    deleteApplicationRegister,
    getApplicationRegisterById,
    getApplicationRegisters,
    updateApplicationRegister,
} from '/@/store/application-register/apiProviders';
import type { ApplicationRegisterInput } from '/@/types/application-register';
import { invalidate } from '/@/utils/swr';

const LIST_KEY = 'useApplicationRegisters';
const BY_ID_KEY = 'useApplicationRegisterById';

export const useApplicationRegisters = (filters: ApplicationRegisterFilters = {}) => {
    const key = filters.field ? { key: LIST_KEY, ...filters } : null;
    return useSWR(key, () => getApplicationRegisters(filters), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useApplicationRegisterById = (id?: number) => {
    const key = id ? { key: BY_ID_KEY, id } : null;
    return useSWR(key, () => getApplicationRegisterById(id as number), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useMutateCreateApplicationRegister = () =>
    useSWRMutation(
        { key: 'createApplicationRegister' },
        (_key, { arg }: { arg: ApplicationRegisterInput }) => createApplicationRegister(arg),
        { onSuccess: () => invalidate(LIST_KEY) }
    );

export const useMutateUpdateApplicationRegister = () =>
    useSWRMutation(
        { key: 'updateApplicationRegister' },
        (_key, { arg }: { arg: { id: number } & ApplicationRegisterInput }) => {
            const { id, ...data } = arg;
            return updateApplicationRegister(id, data);
        },
        {
            onSuccess: () => {
                invalidate(LIST_KEY);
                invalidate(BY_ID_KEY);
            },
        }
    );

export const useMutateDeleteApplicationRegister = () =>
    useSWRMutation(
        { key: 'deleteApplicationRegister' },
        (_key, { arg }: { arg: { id: number } }) => deleteApplicationRegister(arg.id),
        { onSuccess: () => invalidate(LIST_KEY) }
    );
